import React from 'react';
import clsx from 'clsx';

/**
 * How an audit event is read out loud.
 *
 * Extracted from ExpensesDashboard because two places now render the same
 * immutable trail — a live transaction's Details sheet, and a deleted entry in
 * the recovery drawer — and a deletion described as "Deleted" in one place and
 * "delete" in the other reads as two different systems talking about the same
 * money.
 *
 * Nothing here reformats a value. Snapshot amounts arrive as exact-cent
 * strings; re-parsing them through Number() is how a cent goes missing.
 */

export const HISTORY_ACTION_LABELS = {
  create: 'Created',
  settlement: 'Settlement recorded',
  update: 'Edited',
  delete: 'Deleted',
  restore: 'Restored',
  fx_convert: 'Currency converted',
  import_replace: 'Replaced by Splitwise import',
};

export const HISTORY_ACTION_TONES = {
  create: 'bg-slate-900 text-white',
  settlement: 'bg-emerald-600 text-white',
  update: 'bg-amber-500 text-white',
  delete: 'bg-rose-600 text-white',
  restore: 'bg-sky-600 text-white',
  fx_convert: 'bg-indigo-600 text-white',
  import_replace: 'bg-slate-500 text-white',
};

// "household" is the shared web login, not a person — this app has one
// password and cannot tell Kate from Nav. Say so rather than implying a
// signature that does not exist.
export const describeActor = (event) => {
  if (!event || !event.actor) return 'Unattributed';
  if (event.actor_source === 'header') return event.actor;
  if (event.actor_source === 'melissa') return 'Assistant';
  return 'Shared login';
};

export const formatHistoryStamp = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

// Snapshot values are exact-cent strings or nulls; render them readably
// without ever reformatting the number itself.
export const formatHistoryValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return String(value);
};

/** One event: what happened, when, who, why, and the field-level diff. */
export const HistoryEvent = ({ event }) => (
  <li className="rounded-2xl border border-slate-200 bg-white px-4 py-3">
    <div className="flex flex-wrap items-center gap-2">
      <span className={clsx('rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em]', HISTORY_ACTION_TONES[event.action] || 'bg-slate-200 text-slate-700')}>
        {HISTORY_ACTION_LABELS[event.action] || event.action}
      </span>
      <span className="text-xs text-slate-500">{formatHistoryStamp(event.occurred_at)}</span>
      <span className="text-xs text-slate-400">· {describeActor(event)}</span>
    </div>
    {event.reason ? <p className="mt-2 text-sm italic text-slate-600">“{event.reason}”</p> : null}
    {event.changes && event.changes.length > 0 ? (
      <ul className="mt-2 space-y-1 text-sm">
        {event.changes.map((change, index) => (
          <li key={`${event.id}-${change.field}-${index}`} className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-slate-500">{change.label}</span>
            <span className="text-rose-600 line-through">{formatHistoryValue(change.before)}</span>
            <span className="text-slate-400">→</span>
            <span className="font-medium text-emerald-700">{formatHistoryValue(change.after)}</span>
          </li>
        ))}
      </ul>
    ) : null}
    {event.action === 'fx_convert' && event.context ? (
      <p className="mt-2 text-sm text-slate-600">
        {event.context.source_currency} {event.context.source_amount} → {event.context.target_currency} {event.context.target_amount}
        <span className="text-slate-400"> · rate {event.context.rate} ({event.context.rate_source}, {event.context.rate_as_of})</span>
      </p>
    ) : null}
  </li>
);

/**
 * The trail, oldest first — the order the server returns and the order the
 * events actually happened in.
 *
 * An empty trail is stated, not hidden: changes made before change history
 * existed are genuinely unreconstructable, and a blank space would read as
 * "nothing ever happened to this record".
 */
export const HistoryEventList = ({ events, emptyMessage }) => {
  if (!events || events.length === 0) {
    return (
      <p className="mt-3 text-sm text-slate-500">
        {emptyMessage || 'No recorded history. Changes made before change history was added are not reconstructable.'}
      </p>
    );
  }
  return (
    <ol className="mt-3 space-y-3">
      {events.map((event) => <HistoryEvent key={event.id} event={event} />)}
    </ol>
  );
};

export default HistoryEventList;
